import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Crosshair, ExternalLink, Play, Repeat, Sparkles } from "lucide-react";
import { apiGet } from "../../../Api/Client.js";
import CoachingAssignments from "./CoachingAssignments.jsx";
import LoadFailed from "./LoadFailed.jsx";
import PersonPicker from "./PersonPicker.jsx";
import { stamp } from "./CallPlayer.jsx";
import { MISS_AT, plural, shortDate } from "./callMeta.js";
import { PlaybookLine } from "./CallCard.jsx";

// "Your patterns" — the same miss, call after call.
//
// A single bad call is noise; this panel is what turns the scorecard from a
// report into a coaching agenda. It used to be a flat list of status words
// (CHRONIC, RECURRING, WATCH) beside counts and a sentence that restated the
// counts, and nobody could tell what the words meant or why one row outranked
// another. So it now answers three questions, in this order:
//
//   1. What do I work on next?   One card, at the top. Exactly one drill: a
//                                rep shown six failings at once acts on none.
//   2. What keeps happening?     Rows grouped in plain words, each with a strip
//                                of squares, one per call it came up on — red
//                                missed, green held — so "9 of 13" is something
//                                you can see rather than something you're told.
//   3. What's getting better?    Progress has to be as visible as failure, or
//                                people stop opening the page.
//
// Rules held from before: every count renders with its denominator, and
// "not tested" is never shown as a pass or a fail.

const STATUS = {
  chronic:   { label: "Every week",  color: "var(--red-600, #c0392b)" },
  recurring: { label: "Repeating",   color: "var(--red-600, #c0392b)" },
  new:       { label: "New",         color: "var(--blue-600)" },
  watch:     { label: "Too early",   color: "var(--c-500)" },
  improving: { label: "Improving",   color: "var(--green-600)" },
  resolved:  { label: "Fixed",       color: "var(--green-600)" },
};

// Plain-language groups. The status still decides the group; the reader only
// needs the group's name to know what kind of row they are looking at.
const GROUPS = [
  { key: "repeating", title: "Keeps happening", statuses: ["chronic", "recurring"],
    hint: "Missed again and again. These are habits, not bad days." },
  { key: "new", title: "New this week", statuses: ["new"],
    hint: "First miss this month. One call, so not a habit yet." },
  { key: "better", title: "Getting better", statuses: ["improving", "resolved"],
    hint: "Missed less than before, on real calls, not because they didn't come up." },
  { key: "watch", title: "Too early to tell", statuses: ["watch"],
    hint: "Missed, but on too few calls to say which way it's going.", collapsed: true },
];

const muted = { color: "var(--c-500)" };
const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px" };
const RED = "var(--red-600, #c0392b)";
const GREEN = "var(--green-600)";

// One square per call the parameter came up on, oldest to newest. Red is a
// miss (at or below MISS_AT), green is held. Clicking one opens that call.
function CallStrip({ history = [], onOpenCall, size = 11 }) {
  if (!history.length) return null;
  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 3, alignItems: "center" }}
          aria-label={`${history.filter((h) => h.score <= MISS_AT).length} missed of ${history.length} calls, oldest first`}>
      {history.map((h) => {
        const missed = h.score <= MISS_AT;
        return (
          <button key={h.call_id} type="button"
                  onClick={(e) => { e.stopPropagation(); onOpenCall && onOpenCall(h.call_id); }}
                  title={`${shortDate(h.date)} · ${h.score}/10 · ${missed ? "missed" : "held"}${onOpenCall ? " · open this call" : ""}`}
                  aria-label={`${shortDate(h.date)}: ${h.score} out of 10, ${missed ? "missed" : "held"}`}
                  style={{ width: size, height: size, padding: 0, borderRadius: 3,
                           cursor: onOpenCall ? "pointer" : "default",
                           border: `1.5px solid ${missed ? RED : GREEN}`,
                           background: missed ? RED : "transparent" }} />
        );
      })}
    </span>
  );
}

function Legend() {
  const sq = (missed) => (
    <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, verticalAlign: "-1px",
                   border: `1.5px solid ${missed ? RED : GREEN}`, background: missed ? RED : "transparent" }} />
  );
  return (
    <span style={{ fontSize: 11, ...muted, display: "inline-flex", gap: 10, flexWrap: "wrap" }}>
      <span>{sq(true)} missed ({MISS_AT} or below)</span>
      <span>{sq(false)} held</span>
      <span>one square per call, oldest → newest</span>
    </span>
  );
}

// One moment behind a miss: what was said, who said it, why it scored low and
// what to try. A buyer's sentence is labelled as the buyer's. It used to be
// printed as the rep's "worst moment", which they never said and could not
// act on.
function Moment({ example, onOpenCall, compact = false }) {
  if (!example) return null;
  const who = example.quote_speaker;
  const where = [shortDate(example.occurred_on), example.company_name || example.call_label]
    .filter(Boolean).join(" · ");

  return (
    <div style={{ marginTop: compact ? 6 : 8, paddingLeft: 10, borderLeft: "2px solid var(--c-300)" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", fontSize: 11, ...muted }}>
        <strong style={{ color: example.score <= 3 ? RED : "var(--c-700)", fontVariantNumeric: "tabular-nums" }}>
          {example.score}/10
        </strong>
        {where && <span>{where}</span>}
        {onOpenCall && (
          <button type="button" onClick={() => onOpenCall(example.call_score_id, example.at_seconds)}
                  style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer",
                           font: "inherit", fontSize: 11, color: "var(--blue-600)", fontWeight: 600,
                           display: "inline-flex", alignItems: "center", gap: 3 }}>
            {example.at_seconds != null
              ? <><Play size={10} /> hear it at {stamp(example.at_seconds)}</>
              : <><ExternalLink size={10} /> open the call</>}
          </button>
        )}
      </div>

      {example.quote ? (
        <div style={{ marginTop: 3, fontSize: 12.5, lineHeight: 1.5, color: "var(--c-800)" }}>
          <span style={{ fontSize: 10.5, ...muted, textTransform: "uppercase", letterSpacing: ".04em",
                         marginRight: 6 }}>
            {who ? `${who} said` : "You said"}
          </span>
          <span style={{ fontStyle: "italic" }}>“{example.quote}”</span>
        </div>
      ) : (
        // A voice score is backed by how the call sounded, not by a line, so
        // there is nothing to quote. Said plainly rather than rendering “”.
        !example.why && (
          <div style={{ marginTop: 3, fontSize: 12, ...muted }}>
            Scored on how you sounded, not on one line. Open the call to hear it.
          </div>
        )
      )}
      {example.why && (
        <div style={{ marginTop: 3, fontSize: 12.5, color: "var(--c-700)", lineHeight: 1.5 }}>{example.why}</div>
      )}
      {!compact && example.try_next && (
        <div style={{ marginTop: 3, fontSize: 12.5, color: "var(--c-800)", lineHeight: 1.5 }}>
          <span style={{ ...muted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em",
                         marginRight: 6 }}>Try</span>
          {example.try_next}
        </div>
      )}
    </div>
  );
}

// The rep's own best moment on this skill: proof they can already do it, in
// their own words, on a real call. Never a colleague's line.
function BestMoment({ moment, onOpenCall }) {
  if (!moment) return null;
  const where = [shortDate(moment.call_date), moment.company_name || moment.call_label].filter(Boolean).join(" · ");
  return (
    <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8,
                  background: "rgba(40,180,120,.08)", border: "1px solid rgba(40,180,120,.25)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 10.5,
                    textTransform: "uppercase", letterSpacing: ".04em", color: "var(--green-600)", fontWeight: 700 }}>
        <Sparkles size={12} /> You've done this before
        <span style={{ ...muted, fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 11 }}>
          {moment.score}/10 · {where}
        </span>
        {onOpenCall && (
          <button type="button" onClick={() => onOpenCall(moment.call_id, moment.at_seconds)}
                  style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer",
                           fontFamily: "inherit", fontSize: 11, color: "var(--blue-600)", fontWeight: 600,
                           textTransform: "none", letterSpacing: 0, display: "inline-flex", alignItems: "center", gap: 3 }}>
            <Play size={10} /> {moment.at_seconds != null ? `hear it at ${stamp(moment.at_seconds)}` : "open the call"}
          </button>
        )}
      </div>
      {moment.quote
        ? <div style={{ marginTop: 3, fontSize: 12.5, color: "var(--c-800)", fontStyle: "italic" }}>“{moment.quote}”</div>
        : moment.why && <div style={{ marginTop: 3, fontSize: 12.5, color: "var(--c-800)" }}>{moment.why}</div>}
      <div style={{ marginTop: 2, fontSize: 11.5, ...muted }}>Do that again.</div>
    </div>
  );
}

function WindowBreakdown({ mistake }) {
  const windows = [
    ["7 days", mistake.misses_7d, mistake.opportunities_7d],
    ["14 days", mistake.misses_14d, mistake.opportunities_14d],
    ["30 days", mistake.misses_30d, mistake.opportunities_30d],
  ];
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11.5, ...muted, marginTop: 8 }}>
      {windows.map(([label, m, o]) => (
        <span key={label}>
          <strong style={{ color: "var(--c-700)", fontWeight: 600 }}>{label}:</strong>{" "}
          {o ? `missed ${m} of ${plural(o, "call")}` : "didn't come up"}
        </span>
      ))}
      {mistake.first_seen && (
        <span>first missed {shortDate(mistake.first_seen)}</span>
      )}
    </div>
  );
}

function MistakeRow({ mistake, expanded, onToggle, onOpenCall }) {
  const status = STATUS[mistake.status] || STATUS.watch;
  const examples = mistake.examples || [];

  return (
    <div style={{ padding: "10px 0", borderTop: "1px solid var(--c-200)" }}>
      <button type="button" onClick={onToggle} aria-expanded={expanded}
              style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 10,
                       alignItems: "center", width: "100%", textAlign: "left", padding: 0,
                       border: "none", background: "transparent", cursor: "pointer", font: "inherit" }}>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--c-900)" }}>{mistake.label}</span>
            <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".05em",
                           textTransform: "uppercase", color: status.color }}>
              {status.label}
            </span>
          </span>
          <span style={{ display: "block", marginTop: 2, fontSize: 12, ...muted }}>
            {mistake.status_note}
          </span>
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: "var(--c-700)", fontVariantNumeric: "tabular-nums",
                         whiteSpace: "nowrap" }}>
            {mistake.misses} of {plural(mistake.opportunities, "call")}
          </span>
          {expanded ? <ChevronUp size={14} color="var(--c-500)" /> : <ChevronDown size={14} color="var(--c-500)" />}
        </span>
      </button>

      <div style={{ marginTop: 6 }}>
        <CallStrip history={mistake.history} onOpenCall={onOpenCall} />
      </div>

      {expanded && (
        <div style={{ marginTop: 4 }}>
          {examples.length === 0 && (
            <p style={{ margin: "6px 0 0", fontSize: 12, ...muted }}>No moment recorded for these misses.</p>
          )}
          {mistake.examples_in_window === false && examples.length > 0 && (
            <p style={{ margin: "6px 0 0", fontSize: 11.5, ...muted }}>
              Not missed in this window. The last time it was:
            </p>
          )}
          {examples.map((ex) => (
            <Moment key={`${ex.call_score_id}-${ex.at_seconds}`} example={ex} onOpenCall={onOpenCall} />
          ))}
          <BestMoment moment={mistake.best_moment} onOpenCall={onOpenCall} />
          <WindowBreakdown mistake={mistake} />
        </div>
      )}
    </div>
  );
}

// The one thing to work on. It carries everything needed to act without
// opening anything else: what good sounds like, the worst recent moment in the
// words actually said, and a line to try.
function FocusCard({ mistake, onOpenCall, playbookLink, fromCoach = false, isManager = false }) {
  const worst = (mistake.examples || [])[0];
  return (
    <div style={{ marginTop: 12, padding: "14px 16px", borderRadius: 12,
                  border: "1px solid var(--blue-600)", background: "rgba(80,120,255,.07)" }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 700,
                    letterSpacing: ".06em", textTransform: "uppercase", color: "var(--blue-600)" }}>
        <Crosshair size={12} /> Work on this next
        {/* Whose call it was. A coach's assignment outranks the ranking while
            the skill is still being missed (patterns.analyse). */}
        {fromCoach && (
          <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 600, color: "var(--c-600)" }}>
            · {isManager ? "assigned in a 1:1" : "your coach's pick"}
          </span>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10,
                    flexWrap: "wrap", marginTop: 4 }}>
        <strong style={{ fontSize: 16, color: "var(--c-900)" }}>{mistake.label}</strong>
        <span style={{ fontSize: 12.5, color: "var(--c-700)", fontVariantNumeric: "tabular-nums" }}>
          missed on {mistake.misses} of {plural(mistake.opportunities, "call")}
        </span>
      </div>
      <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "var(--c-700)" }}>{mistake.status_note}</p>
      <div style={{ marginTop: 8 }}>
        <CallStrip history={mistake.history} onOpenCall={onOpenCall} size={13} />
      </div>

      {mistake.ten && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--c-800)", lineHeight: 1.5 }}>
          <span style={{ ...muted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em",
                         marginRight: 6 }}>What a 10 sounds like</span>
          {mistake.ten}
        </div>
      )}

      {worst && (
        <div style={{ marginTop: 8 }}>
          <div style={{ ...muted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em" }}>
            {mistake.examples_in_window === false ? "Last time it was missed" : "Lowest moment this window"}
          </div>
          <Moment example={worst} onOpenCall={onOpenCall} />
        </div>
      )}

      <BestMoment moment={mistake.best_moment} onOpenCall={onOpenCall} />
      <PlaybookLine link={playbookLink} />
    </div>
  );
}

export default function PatternsPanel({ employeeId = null, track = "junior", people = [],
                                       onSelectEmployee, isManager = false, onOpenCall,
                                       playbookLinks = {} }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [windowDays, setWindowDays] = useState(track === "senior" ? 30 : 7);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [showEarlier, setShowEarlier] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // An AE runs a couple of demos a week, so their panel leads with 30 days;
  // the track arrives after the person does, so the default follows it. Only a change of track
  // resets the window: switching person keeps the 7/14/30 the viewer chose.
  useEffect(() => { setWindowDays(track === "senior" ? 30 : 7); }, [track]);

  // Another person: drop the last person's numbers and opened rows at once, so nothing of theirs is
  // read under the new name while the answer is on its way.
  useEffect(() => { setData(null); setExpandedKeys(new Set()); }, [employeeId]);

  useEffect(() => {
    let cancelled = false;
    setError("");
    const query = new URLSearchParams({ window: String(windowDays) });
    if (employeeId) query.set("employee", String(employeeId));
    apiGet(`/RevOps/recurring-mistakes/?${query}`)
      .then((payload) => !cancelled && setData(payload))
      .catch((err) => !cancelled && setError(err?.message || "Couldn't load your patterns."));
    return () => { cancelled = true; };
  }, [employeeId, windowDays, attempt]);

  const mistakes = data?.mistakes || [];
  const drill = mistakes.find((m) => m.parameter_key === data?.drill_this_week) || null;
  const grouped = useMemo(() => GROUPS.map((g) => ({
    ...g,
    rows: mistakes.filter((m) => g.statuses.includes(m.status) && m !== drill),
  })).filter((g) => g.rows.length), [mistakes, drill]);

  // The title, the person picker and the window buttons stay on screen whatever state the data is in.
  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10,
                  flexWrap: "wrap" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5,
                     fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                     color: "var(--c-600)" }}>
        <Repeat size={13} /> {isManager ? "Patterns" : "Your patterns"}
      </span>
      <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <PersonPicker people={people} value={employeeId} onChange={onSelectEmployee} />
        <span style={{ display: "flex", gap: 3 }}>
          {[7, 14, 30].map((d) => (
            <button key={d} onClick={() => setWindowDays(d)} style={{
              border: "none", cursor: "pointer", fontSize: 11.5, padding: "4px 9px", borderRadius: 7,
              background: windowDays === d ? "var(--c-100, rgba(120,120,120,.14))" : "transparent",
              fontWeight: windowDays === d ? 700 : 400,
              color: windowDays === d ? "var(--blue-600)" : "var(--c-500)",
            }}>{d} days</button>
          ))}
        </span>
      </span>
    </div>
  );

  if (error) return <div style={card}>{header}<LoadFailed message={error} onRetry={() => setAttempt((n) => n + 1)} /></div>;
  if (!data) return <div style={card}>{header}<p style={{ ...muted, margin: "12px 0 0" }}>Loading…</p></div>;

  const earlier = data.earlier || [];
  const dims = data.by_dimension || {};
  const assignments = (
    <CoachingAssignments employeeId={employeeId} isManager={isManager}
                         drillKey={data.drill_this_week} drillLabel={drill?.label || ""} />
  );
  const toggleRow = (key) => setExpandedKeys((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const toggleGroup = (key) => setOpenGroups((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  return (
    <div style={card}>
      {header}

      <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "var(--c-700)" }}>
        Last {data.window_days} days · {plural(data.calls_in_window, "call")} scored
        {(dims.tonality || dims.technicality) && (
          <span style={muted}>
            {[["voice & delivery", dims.tonality], ["substance", dims.technicality]]
              .filter(([, d]) => d)
              .map(([label, d]) => ` · ${label}: a miss on ${d.calls_low} of ${plural(d.calls, "call")}`)
              .join("")}
          </span>
        )}
      </p>
      <div style={{ marginTop: 4 }}><Legend /></div>

      {data.calls_in_window === 0 ? (
        // Never "fixed" off the back of an empty week. A rep who made no calls
        // has not fixed anything.
        <>
          <p style={{ fontSize: 12.5, ...muted, margin: "12px 0 0" }}>
            No scored calls in the last {data.window_days} days, so there's nothing to read yet.
            {data.window_days < 30 ? " Try a longer window." : ""}
          </p>
          {assignments}
        </>
      ) : mistakes.length === 0 ? (
        <>
          <p style={{ fontSize: 12.5, ...muted, margin: "12px 0 0" }}>
            No misses in this window. Nothing to drill.
          </p>
          {assignments}
        </>
      ) : (
        <>
          {drill && <FocusCard mistake={drill} onOpenCall={onOpenCall}
                               playbookLink={playbookLinks[drill.parameter_key]}
                               fromCoach={data.drill_source === "coach"} isManager={isManager} />}
          {/* What the coach assigned, and whether it moved since -- next to
              the drill, not below every other row on the page. */}
          {assignments}

          {grouped.map((g) => {
            const open = g.collapsed ? openGroups.has(g.key) || grouped.length === 1 : true;
            return (
              <div key={g.key} style={{ marginTop: 16 }}>
                <button type="button" onClick={() => g.collapsed && toggleGroup(g.key)}
                        style={{ display: "flex", alignItems: "baseline", gap: 8, width: "100%",
                                 padding: 0, border: "none", background: "transparent", textAlign: "left",
                                 cursor: g.collapsed ? "pointer" : "default", font: "inherit" }}>
                  {g.collapsed && (open ? <ChevronUp size={13} color="var(--c-500)" />
                                        : <ChevronDown size={13} color="var(--c-500)" />)}
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--c-900)" }}>
                    {g.title} <span style={{ ...muted, fontWeight: 400 }}>({g.rows.length})</span>
                  </span>
                  <span style={{ fontSize: 11.5, ...muted }}>{g.hint}</span>
                </button>
                {open && (
                  <div style={{ marginTop: 4 }}>
                    {g.rows.map((m) => (
                      <MistakeRow key={m.parameter_key} mistake={m}
                                  expanded={expandedKeys.has(m.parameter_key)}
                                  onToggle={() => toggleRow(m.parameter_key)}
                                  onOpenCall={onOpenCall} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* Missed earlier in the last 30 days, but not in this window. Kept off
          the agenda and not hidden either: a manager may want them. */}
      {earlier.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px solid var(--c-200)" }}>
          <button type="button" onClick={() => setShowEarlier((v) => !v)}
                  style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0,
                           display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12,
                           color: "var(--c-600)", fontWeight: 600 }}>
            {showEarlier ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            Missed earlier this month, not in the last {data.window_days} days ({earlier.length})
          </button>
          {showEarlier && earlier.map((m) => (
            <div key={m.parameter_key} style={{ padding: "7px 0 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <span style={{ fontSize: 12.5, color: "var(--c-800)" }}>{m.label}</span>
                <span style={{ fontSize: 11.5, ...muted, fontVariantNumeric: "tabular-nums" }}>
                  missed {m.misses_30d} of {plural(m.opportunities_30d, "call")} in 30 days
                </span>
              </div>
              <div style={{ fontSize: 11.5, ...muted }}>{m.status_note}</div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
